import { useMemo, useState, type ReactNode } from 'react';
import qrcode from 'qrcode-generator';
import type { GuestContext } from '@guest/shared';
import { Icon } from '../lib/icons.js';
import { Panel } from './ui.js';
import { detectPlatform } from '../lib/format.js';

type Wifi = NonNullable<GuestContext['wifi']>;

/** Escapes the characters that are structural in a WIFI: QR payload. */
const esc = (v: string): string => v.replace(/([\;,":])/g, '\\$1');

function CopyChip({ value, label }: { value: string; label: string }): ReactNode {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="chip"
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setDone(true);
          window.setTimeout(() => setDone(false), 1800);
        });
      }}
    >
      <Icon name={done ? 'check' : 'share'} size={14} />
      {done ? 'Copied' : label}
    </button>
  );
}

/**
 * There is no way for a web app to join a network on the guest's behalf. iOS
 * configuration profiles technically can, but cost six steps through Settings
 * to save typing one word — so the honest primary action is "copy the
 * password", with the QR there for a second device or Android's scanner.
 */
export function WifiCard({ wifi }: { wifi: Wifi }): ReactNode {
  const [reveal, setReveal] = useState(false);
  const [copied, setCopied] = useState(false);
  const platform = detectPlatform();

  // Rendered as an SVG path so it stays sharp at any size and needs no canvas.
  const qr = useMemo(() => {
    const payload = `WIFI:T:${wifi.security};S:${esc(wifi.ssid)};P:${esc(wifi.password)};;`;
    const q = qrcode(0, 'M');
    q.addData(payload);
    q.make();
    return q.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
  }, [wifi]);

  const hint =
    platform === 'ios'
      ? 'Then Settings → Wi-Fi → ' + wifi.ssid + ' → paste.'
      : platform === 'android'
        ? 'Then Settings → Network & internet → Wi-Fi → ' + wifi.ssid + ' → paste.'
        : 'Then pick ' + wifi.ssid + ' from your wifi menu and paste.';

  return (
    <Panel title="House wifi" icon="wifi" className="stack">
      <div className="wifi-rows">
        <div className="wifi-row">
          <span className="k">Network</span>
          <span className="v">{wifi.ssid}</span>
          <span className="actions">
            <CopyChip value={wifi.ssid} label="Copy" />
          </span>
        </div>
        <div className="wifi-row">
          <span className="k">Password</span>
          <span className="v mono">
            {reveal ? wifi.password : '•'.repeat(Math.min(wifi.password.length, 12))}
          </span>
          <span className="actions">
            <button type="button" className="chip" onClick={() => setReveal((r) => !r)}>
              <Icon name={reveal ? 'close' : 'info'} size={14} />
              {reveal ? 'Hide' : 'Show'}
            </button>
          </span>
        </div>
      </div>

      <button
        type="button"
        className="btn btn-primary btn-block"
        onClick={() => {
          void navigator.clipboard?.writeText(wifi.password).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2400);
          });
        }}
      >
        <Icon name={copied ? 'check' : 'key'} size={17} />
        {copied ? 'Password copied' : 'Copy password'}
      </button>
      <p className="wifi-note">{hint}</p>

      <details className="wifi-qr">
        <summary>
          <Icon name="camera" size={14} />
          Scan from another device
        </summary>
        <div className="qr" dangerouslySetInnerHTML={{ __html: qr }} />
        <p>
          {platform === 'android'
            ? 'Or here: Settings → Wi-Fi → Add network → scan this code.'
            : 'Point another phone’s camera at this to join.'}
        </p>
      </details>
    </Panel>
  );
}
